/**
 * E06-F05-T05 — "AI check" screen: the manual, per-company trigger for the paid
 * scrape-first Haiku chain, plus the provenance panel behind each result.
 *
 * Design contour: docs/proposals/research-ai-chain-scrape-first.md §7 (candidate
 * table + checkbox column + per-row trigger + bulk action-bar with a confirmed count
 * + per-company status chips + polling + sources panel) and the PM resolution #5 in
 * docs/superpowers/specs/2026-08-17-scrape-haiku-web-research-design.md §1.3 (the
 * user types the batch volume, pre-filled 10, backstop 50).
 *
 * The screen exists because after E06-F05-T04 discovery stops at `deduped` and NOTHING
 * paid runs by itself: a row sits at `aiStatus='idle'` until someone clicks here.
 * Guarantees encoded in this view:
 *   - no paid call in any effect/query — only in a click handler (no-automatic-AI);
 *   - a multi-company run is gated by a ConfirmDialog that names the exact count;
 *   - every company shows its own stage chip (collect / extract / verify / ready /
 *     failed) and a failed company gets its own Retry button;
 *   - "Sources" opens the pages we downloaded and the raw text as it was BEFORE the
 *     model saw it, so a verdict can always be traced to what it was based on.
 *
 * All non-React rules live in the DOM-free `./researchAiChainModel.ts`; the wire
 * shapes + hooks in `./researchAiChainApi.ts`. No `innerHTML` — the collected page
 * text is rendered as escaped text inside a <pre>.
 */
import { Fragment, useEffect, useMemo, useRef, useState, type JSX } from 'react';
import {
  AI_RUN_UNCONFIRMED_DOMAIN_WARNING,
  WEBSITE_UNCONFIRMED_LABEL,
  type ManualCheckInput,
  type PipelineRow,
} from '@eer/shared';
import { ConfirmDialog, Drawer } from '../ui/index.js';
// E07-F04-T09 — the shared name/website rule (never a row id in a name cell).
import { CompanyName, CompanyDomain, companyAriaLabel } from './CompanyLabel.js';
import { useToast } from '../ui/ToastContext.js';
import {
  AI_RUN_BATCH_CAP,
  AI_RUN_BATCH_DEFAULT,
  aiStatusMeta,
  canCheckManually,
  aiCheckPriceLine,
  confirmMessage,
  manualCheckMarkText,
  countUnconfirmedDomains,
  hasUnconfirmedDomain,
  unconfirmedDomainsNote,
  formatBytes,
  formatCollectedAt,
  isRetryable,
  isRunnable,
  parseVolume,
  pruneSelection,
  rowFailure,
  selectableIds,
  takeFirst,
  toggleId,
  validateBatch,
} from './researchAiChainModel.js';
import {
  useAiChainRows,
  useAiCheckQuote,
  useCollectedSources,
  useRerunAiOne,
  useRunAiBatch,
  useRunAiOne,
  useSubmitManualCheck,
} from './researchAiChainApi.js';
// E06-F05-T16 — the site blocked the AI check → a person checks it by hand.
import { ManualCheckDrawer } from './ManualCheckDrawer.js';
import { ScoreExplainToggle, ScoreExplainDetailsRow } from './ScoreExplain.js';
import { PriorCollectionNote } from './PriorCollectionNote.js';
// E06-F05-T14 — discovery → AI check → scoring → review explanation.
import { ResearchProcessSteps } from './ResearchProcessSteps.js';
import { pipelineRowExplanation } from './scoreExplainModel.js';
// E06-F05-T15 — technical (raw) blocks: label + the one visibility switch.
import { RAW_DETAILS_LABEL, useCanSeeRawDetails } from './rawDetails.js';
// E06-F03-T13-S03 — AI-check preflight: no website → not started.
import {
  ENTER_WEBSITE_FIRST,
  aiRunErrorMessage,
  batchStartedMessage,
  countNoWebsiteSkips,
  noWebsiteSkipMessage,
  rowWebsite,
} from './referenceSiteModel.js';
// E07-F11-T04 — the Jev triage «check first» bucket is preselected (the human still decides).
import { aiCheckPreselection, TRIAGE_BUCKET_LABEL } from './researchTriageModel.js';

// ---------------------------------------------------------------------------
// public props
// ---------------------------------------------------------------------------

export interface ResearchAiChainViewProps {
  /** The research request whose long-list is being checked. */
  readonly requestId: number;
  /** Request title for the header (falls back to the id). */
  readonly title?: string;
  /** Back to the Run screen. */
  readonly onBack?: () => void;
  /** Continue to the Review long-list. */
  readonly onReview?: () => void;
  /** E07-F04-T12 — open an earlier request (the "collected in" link of a row). */
  readonly onOpenRequest?: (requestId: number, title: string) => void;
}

// ---------------------------------------------------------------------------
// per-company status chip (acceptance #3)
// ---------------------------------------------------------------------------

function StatusChip({ row }: { row: PipelineRow }): JSX.Element {
  const meta = aiStatusMeta(row.aiStatus);
  // E06-F05-T08: the tooltip carries the human sentence, never the raw `GET … → HTTP 403`.
  const failure = rowFailure(row);
  return (
    <span
      data-testid={`aichain-status-${row.id}`}
      data-ai-status={row.aiStatus}
      title={failure?.message ?? undefined}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${meta.chipClass}`}
    >
      <i className={`fa-solid ${meta.icon}`} aria-hidden />
      {meta.label}
    </span>
  );
}

/** E06-F05-T16 — the "entered by hand" mark next to the status chip. */
function ManualMark({ row }: { row: PipelineRow }): JSX.Element | null {
  if (!row.manualCheck) return null;
  return (
    <span
      data-testid={`aichain-manual-mark-${row.id}`}
      title={row.manualCheck.url ?? undefined}
      className="mt-1 flex w-fit items-center gap-1 rounded bg-sky-50 px-1.5 py-0.5 text-[11px] font-semibold text-sky-700"
    >
      <i className="fa-solid fa-user-check" aria-hidden />
      {manualCheckMarkText(row.manualCheck)}
    </span>
  );
}

// ---------------------------------------------------------------------------
// failure note (E06-F05-T08)
// ---------------------------------------------------------------------------

/**
 * Why a company's check failed, in words a recruiter can act on.
 *
 * Before E06-F05-T08 this cell printed the scraper's raw string — `GET
 * http://acme.com/ → HTTP 403` — as its ONLY text. Now the main line is the natural
 * -language sentence from `rowFailure` ("The company website blocked automated access
 * — open it manually to check.") and the technical string stays one click away inside
 * a collapsed <details>, so nothing is lost for support/debugging.
 */
function FailureNote({ row }: { row: PipelineRow }): JSX.Element | null {
  const failure = rowFailure(row);
  if (!failure) return null;
  return (
    <div
      data-testid={`aichain-error-${row.id}`}
      data-error-code={failure.code}
      className="mt-1 max-w-xs text-xs text-red-600"
    >
      {failure.message}
      {failure.detail ? (
        <details className="mt-0.5">
          <summary
            data-testid={`aichain-error-detail-toggle-${row.id}`}
            className="cursor-pointer font-normal text-slate-400 hover:text-slate-600"
          >
            Technical details
          </summary>
          <span
            data-testid={`aichain-error-detail-${row.id}`}
            className="mt-0.5 block break-all font-mono text-slate-500"
          >
            {failure.detail}
          </span>
        </details>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// collected-sources panel (acceptance #5)
// ---------------------------------------------------------------------------

function SourcesPanel({
  requestId,
  row,
  onClose,
}: {
  requestId: number;
  row: PipelineRow | null;
  onClose: () => void;
}): JSX.Element | null {
  const sources = useCollectedSources(requestId, row?.id ?? null);
  if (!row) return null;
  const data = sources.data;
  return (
    <Drawer
      open
      onClose={onClose}
      title={`${RAW_DETAILS_LABEL} — collected sources — ${companyAriaLabel(row.name)}`}
      width="lg"
      testId="aichain-sources-drawer"
    >
      <div className="space-y-4" data-testid="aichain-sources-body">
        {sources.isLoading ? (
          <p className="text-sm text-slate-500" data-testid="aichain-sources-loading">
            Loading the collected pages…
          </p>
        ) : null}

        {data && !data.collected ? (
          <div
            data-testid="aichain-sources-empty"
            className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
          >
            Nothing collected yet for{' '}
            <b>{data.domain ?? 'this company (website not found — enter it in Review)'}</b>. Run the
            AI check — the pages we download appear here before the model reads them.
          </div>
        ) : null}

        {data && data.collected ? (
          <>
            <div
              data-testid="aichain-sources-summary"
              className="rounded-lg border border-border bg-slate-50 px-4 py-3 text-[13px] text-slate-600"
            >
              <b className="text-slate-800">{data.pages.length}</b> pages ·{' '}
              <b className="text-slate-800">{formatBytes(data.totalBytes)}</b> of visible text ·
              collected {formatCollectedAt(data.collectedAt)}{' '}
              <span className={data.fresh ? 'text-emerald-600' : 'text-amber-600'}>
                ({data.fresh ? 'fresh' : 'stale — re-run to refresh'})
              </span>
              {data.truncated ? (
                <span className="ml-1 text-amber-700">· truncated by the collection caps</span>
              ) : null}
              {/* E06-F05-T15 — say plainly this is debugging data, not a result. */}
              <p className="mt-1 text-xs text-slate-500" data-testid="aichain-sources-raw-note">
                Raw page text as the model received it (HTML, scripts and navigation stripped). Kept
                for checking the research algorithm — you do not need it to review companies.
              </p>
            </div>
            <ul className="space-y-3" data-testid="aichain-sources-pages">
              {data.pages.map((page) => (
                <li
                  key={page.url}
                  data-testid={`aichain-source-page-${page.url}`}
                  className="rounded-lg border border-border bg-white"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-3 py-2">
                    <a
                      href={page.url}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="break-all text-[13px] font-semibold text-blue-700 hover:underline"
                    >
                      {page.url}
                    </a>
                    <span className="text-[11px] uppercase tracking-wider text-slate-400">
                      {page.role} · {formatBytes(page.bytes)} · {page.fetchedVia}
                      {page.truncated ? ' · truncated' : ''}
                    </span>
                  </div>
                  <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-words px-3 py-2 text-[12px] leading-snug text-slate-700">
                    {page.text}
                  </pre>
                </li>
              ))}
            </ul>
          </>
        ) : null}
      </div>
    </Drawer>
  );
}

// ---------------------------------------------------------------------------
// the screen
// ---------------------------------------------------------------------------

export function ResearchAiChainView({
  requestId,
  title,
  onBack,
  onReview,
  onOpenRequest,
}: ResearchAiChainViewProps): JSX.Element {
  const toast = useToast();
  const rowsQuery = useAiChainRows(requestId);
  const runOne = useRunAiOne(requestId);
  const rerunOne = useRerunAiOne(requestId);
  const runBatch = useRunAiBatch(requestId);
  const manualCheck = useSubmitManualCheck(requestId);
  // E06-F05-T16 — which row's manual-check drawer is open (one at a time).
  const [manualRowId, setManualRowId] = useState<number | null>(null);

  const [selected, setSelected] = useState<readonly number[]>([]);
  const [volumeInput, setVolumeInput] = useState<string>(String(AI_RUN_BATCH_DEFAULT));
  const [confirmOpen, setConfirmOpen] = useState(false);
  // E12-F17-T06 — the verdict path + price per company, read (free) only while the dialog is open.
  const quote = useAiCheckQuote(requestId, confirmOpen);
  const [sourcesRowId, setSourcesRowId] = useState<number | null>(null);
  // E06-F05-T15 — the raw sources drawer is a technical block.
  const canSeeRaw = useCanSeeRawDetails();
  // E06-F05-T07 — which row has its score breakdown open (one at a time).
  const [explainRowId, setExplainRowId] = useState<number | null>(null);

  const rows: readonly PipelineRow[] = useMemo(() => rowsQuery.data?.rows ?? [], [rowsQuery.data]);
  // E07-F11-T04 — on the FIRST load only, tick the «check first» rows of the Jev triage (not
  // yet checked, at most the default volume). Only a selection — nothing runs; the person can
  // untick / tick freely and later polls never re-apply it. No triage → nothing ticked (as before).
  const preselectApplied = useRef(false);
  const [preselectedCount, setPreselectedCount] = useState(0);
  useEffect(() => {
    if (preselectApplied.current || !rowsQuery.data) return;
    preselectApplied.current = true;
    const ids = aiCheckPreselection(
      rowsQuery.data.rows,
      Math.min(AI_RUN_BATCH_DEFAULT, AI_RUN_BATCH_CAP),
    );
    if (ids && ids.length > 0) {
      setSelected(ids);
      setPreselectedCount(ids.length);
    }
  }, [rowsQuery.data]);
  // A row that went mid-flight under the user drops out of the selection, so a click
  // can never re-charge a company that is already running.
  const liveSelection = useMemo(() => pruneSelection(selected, rows), [selected, rows]);
  const volume = parseVolume(volumeInput);
  const validation = validateBatch(liveSelection.length, volume);
  const selectable = selectableIds(rows);
  const allSelected = selectable.length > 0 && liveSelection.length === selectable.length;
  const sourcesRow = rows.find((r) => r.id === sourcesRowId) ?? null;
  const manualRow = rows.find((r) => r.id === manualRowId) ?? null;
  // E06-F03-T14 — the batch confirmation warns (never blocks) about unconfirmed websites.
  const unconfirmedNote = unconfirmedDomainsNote(countUnconfirmedDomains(rows, liveSelection));

  function handleToggleAll(): void {
    setSelected(allSelected ? [] : selectable);
  }

  function handleSelectFirst(): void {
    if (volume == null) {
      toast.error('Enter how many companies to check.');
      return;
    }
    setSelected(takeFirst(rows, Math.min(volume, AI_RUN_BATCH_CAP)));
  }

  /** PAID — one company, one explicit click. */
  function handleRunOne(rowId: number): void {
    runOne.mutate(rowId, {
      onSuccess: () => toast.success('AI check started for 1 company.'),
      onError: (err) =>
        toast.error(aiRunErrorMessage(err, 'Could not start the AI check — try again.')),
    });
  }

  /** PAID — retry a failed company (re-scrapes, then re-runs the chain). */
  function handleRetry(rowId: number): void {
    rerunOne.mutate(rowId, {
      onSuccess: () => toast.success('Re-running the failed check.'),
      onError: (err) =>
        toast.error(aiRunErrorMessage(err, 'Could not restart the check — try again.')),
    });
  }

  /** PAID — E07-F04-T12: re-collect a company an earlier request already checked. */
  function handleRefreshPrior(rowId: number): void {
    rerunOne.mutate(rowId, {
      onSuccess: () => toast.success('Collecting the data for this company again.'),
      onError: (err) =>
        toast.error(aiRunErrorMessage(err, 'Could not start the refresh — try again.')),
    });
  }

  /** PAID — the selected set. Fires ONLY from the confirmation dialog. */
  function handleConfirmedBatch(): void {
    setConfirmOpen(false);
    const ids = liveSelection;
    runBatch.mutate(ids, {
      onSuccess: (result) => {
        setSelected([]);
        toast.success(batchStartedMessage(result));
        const noWebsite = countNoWebsiteSkips(result.skipped);
        if (noWebsite > 0) toast.error(noWebsiteSkipMessage(noWebsite));
      },
      onError: () => toast.error('Could not start the AI check — try again.'),
    });
  }

  /** FREE — E06-F05-T16: save what the person saw on the website. */
  function handleSaveManual(rowId: number, body: ManualCheckInput): void {
    manualCheck.mutate(
      { rowId, body },
      {
        onSuccess: () => {
          setManualRowId(null);
          toast.success(
            'Manual check saved — the company is scored and marked as checked manually.',
          );
        },
        onError: (err) =>
          toast.error(aiRunErrorMessage(err, 'Could not save the manual check — try again.')),
      },
    );
  }

  const busy = runOne.isPending || rerunOne.isPending || runBatch.isPending;

  return (
    <div data-testid="research-aichain-view">
      {onBack ? (
        <button
          type="button"
          data-testid="aichain-back"
          onClick={onBack}
          className="mb-4 text-sm text-slate-500 hover:text-slate-800"
        >
          <i className="fa-solid fa-arrow-left mr-1" aria-hidden /> Back to the run
        </button>
      ) : null}

      <div className="max-w-6xl overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg">
        <div className="border-b border-gray-100 bg-slate-900 px-6 py-4 text-white">
          {/* E06-F05-T19 — explicit colour: the base `h2 { color: --color-text-strong }`
              rule beats the inherited text-white, painting navy on navy. Gold like Run. */}
          <h2 className="text-lg font-bold text-[#D4AF37]" data-testid="aichain-title">
            AI check — {title ?? `request #${requestId}`}
          </h2>
          <p className="text-xs text-slate-300">
            Paid, manual and per-company: nothing is checked until you select companies and press
            the button.
          </p>
        </div>

        <div className="p-6">
          {/* E06-F05-T14 — where this screen sits in the research process */}
          <ResearchProcessSteps current="aicheck" testId="aichain-process-steps" />
          <p className="mb-3 text-xs text-slate-500" data-testid="aichain-howto">
            <b>Volume</b> = how many companies to check (max {AI_RUN_BATCH_CAP}).{' '}
            <b>Select first N</b> ticks the first N rows of the table (rows already running are
            skipped); you can also tick rows by hand. <b>Run AI check</b> asks for confirmation,
            then charges per company.
          </p>
          {preselectedCount > 0 ? (
            <p
              data-testid="aichain-triage-preselect"
              className="mb-3 rounded border border-green-200 bg-green-50 px-3 py-2 text-xs text-green-800"
            >
              <i className="fa-solid fa-list-check mr-1" aria-hidden />
              {preselectedCount} {preselectedCount === 1 ? 'company' : 'companies'} from the Jev
              triage bucket «{TRIAGE_BUCKET_LABEL.check_first}» preselected (max{' '}
              {Math.min(AI_RUN_BATCH_DEFAULT, AI_RUN_BATCH_CAP)}). Untick or tick rows as you like —
              nothing runs until you press <b>Run AI check</b> and confirm.
            </p>
          ) : null}
          {/* bulk action bar: volume + quick-select + the paid button --------- */}
          <div
            data-testid="aichain-bulk-bar"
            className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-border bg-slate-50 px-4 py-3"
          >
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500">
              Volume
              <input
                type="number"
                min={1}
                max={AI_RUN_BATCH_CAP}
                data-testid="aichain-volume"
                aria-label="How many companies to check"
                value={volumeInput}
                onChange={(e) => setVolumeInput(e.target.value)}
                className="ml-2 w-20 rounded border border-gray-300 px-2 py-1 text-sm font-normal normal-case text-slate-800"
              />
            </label>
            <button
              type="button"
              data-testid="aichain-select-first"
              onClick={handleSelectFirst}
              className="rounded border border-slate-200 px-3 py-1.5 text-sm text-slate-600 hover:bg-white"
            >
              Select first {volume ?? AI_RUN_BATCH_DEFAULT}
            </button>
            <button
              type="button"
              data-testid="aichain-clear-selection"
              onClick={() => setSelected([])}
              disabled={liveSelection.length === 0}
              className="rounded border border-slate-200 px-3 py-1.5 text-sm text-slate-600 hover:bg-white disabled:opacity-40"
            >
              Clear
            </button>
            <div className="flex-1" />
            {validation.message ? (
              <span data-testid="aichain-batch-warning" className="text-xs text-amber-700">
                {validation.message}
              </span>
            ) : null}
            <button
              type="button"
              data-testid="aichain-run-batch"
              disabled={!validation.ok || busy}
              onClick={() => setConfirmOpen(true)}
              className="rounded bg-[#D4AF37] px-4 py-2 text-sm font-bold text-slate-900 shadow hover:bg-[#B5952F] disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-500"
            >
              <i className="fa-solid fa-robot mr-1" aria-hidden /> Run AI check (
              {liveSelection.length} selected)
            </button>
          </div>

          {/* the candidate table ---------------------------------------------- */}
          <div className="overflow-hidden rounded-lg border border-gray-200">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="w-10 px-3 py-2 text-left">
                    <input
                      type="checkbox"
                      data-testid="aichain-select-all"
                      aria-label="Select all companies"
                      checked={allSelected}
                      disabled={selectable.length === 0}
                      onChange={handleToggleAll}
                    />
                  </th>
                  <th className="px-3 py-2 text-left">Company</th>
                  <th className="px-3 py-2 text-left">Grade / score</th>
                  <th className="px-3 py-2 text-left">AI check</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody data-testid="aichain-rows">
                {rows.map((row) => {
                  const checked = liveSelection.includes(row.id);
                  const runnable = isRunnable(row);
                  // E06-F03-T13-S03: the chain scrapes the site first — no site, no run.
                  const hasWebsite = rowWebsite(row) !== null;
                  const explanation = pipelineRowExplanation(row);
                  const explainOpen = explainRowId === row.id;
                  return (
                    <Fragment key={row.id}>
                      <tr className="border-t border-gray-100 align-top">
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            data-testid={`aichain-check-${row.id}`}
                            aria-label={`Select ${companyAriaLabel(row.name)}`}
                            checked={checked}
                            disabled={!runnable}
                            onChange={() => setSelected(toggleId(liveSelection, row.id))}
                          />
                        </td>
                        {/* E07-F04-T09: the row is labelled by the SERVER-resolved
                          name/domain. This cell used to print `row ${row.id}` — the
                          primary key — for every company that had already resolved
                          to a `companies` record (raw_name goes NULL on resolve), so
                          the user saw a database id instead of a company. A row we
                          genuinely cannot name now says so explicitly. */}
                        <td className="px-3 py-2">
                          <CompanyName name={row.name} className="font-semibold text-slate-800" />
                          <CompanyDomain
                            domain={row.domain ?? row.rawDomain}
                            className="block text-xs text-slate-400"
                          />
                          {/* E06-F03-T14 — a code-found reference website nobody
                            confirmed: warn before the paid run, never block it. */}
                          {hasUnconfirmedDomain(row) ? (
                            <span
                              data-testid={`aichain-domain-unconfirmed-${row.id}`}
                              title={AI_RUN_UNCONFIRMED_DOMAIN_WARNING}
                              className="mt-0.5 inline-flex max-w-xs items-start gap-1 rounded bg-amber-50 px-1 text-[10px] font-semibold text-amber-700"
                            >
                              <i className="fa-solid fa-triangle-exclamation mt-0.5" aria-hidden />
                              <span>
                                {WEBSITE_UNCONFIRMED_LABEL} — {AI_RUN_UNCONFIRMED_DOMAIN_WARNING}
                              </span>
                            </span>
                          ) : null}
                          {/* E07-F04-T12 — checked in an earlier request: date + link + Refresh. */}
                          <PriorCollectionNote
                            rowId={row.id}
                            prior={row.priorCollection}
                            onRefresh={() => handleRefreshPrior(row.id)}
                            refreshDisabled={busy || !hasWebsite}
                            refreshDisabledReason={hasWebsite ? undefined : ENTER_WEBSITE_FIRST}
                            onOpenRequest={onOpenRequest}
                          />
                        </td>
                        {/* E06-F05-T07: the percentage is never bare — it is the
                          button that opens the weighted breakdown + the reason the
                          company is on the list at all. */}
                        <td className="px-3 py-2 text-slate-700">
                          <ScoreExplainToggle
                            explanation={explanation}
                            rowId={row.id}
                            open={explainOpen}
                            onToggle={() => setExplainRowId(explainOpen ? null : row.id)}
                          >
                            <span className="font-semibold">{row.grade ?? '—'}</span>
                            <span className="text-xs text-slate-500">
                              {row.score != null ? `${row.score}%` : 'not scored'}
                            </span>
                          </ScoreExplainToggle>
                        </td>
                        <td className="px-3 py-2">
                          <StatusChip row={row} />
                          <ManualMark row={row} />
                          <FailureNote row={row} />
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div className="flex justify-end gap-2">
                            {isRetryable(row) ? (
                              <button
                                type="button"
                                data-testid={`aichain-retry-${row.id}`}
                                disabled={busy || !hasWebsite}
                                title={hasWebsite ? undefined : ENTER_WEBSITE_FIRST}
                                onClick={() => handleRetry(row.id)}
                                className="rounded border border-red-200 px-2.5 py-1 text-xs font-semibold text-red-600 hover:bg-red-50 disabled:opacity-40"
                              >
                                <i className="fa-solid fa-rotate-right mr-1" aria-hidden /> Retry
                              </button>
                            ) : (
                              <button
                                type="button"
                                data-testid={`aichain-run-${row.id}`}
                                disabled={!runnable || busy || !hasWebsite}
                                title={hasWebsite ? undefined : ENTER_WEBSITE_FIRST}
                                onClick={() => handleRunOne(row.id)}
                                className="rounded border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                              >
                                <i className="fa-solid fa-robot mr-1" aria-hidden />
                                {row.aiStatus === 'scored' ? 'Re-check' : 'Run AI'}
                              </button>
                            )}
                            {/* E06-F05-T16 — the site blocked the check: look at it
                              yourself and enter the data (free, no AI call). */}
                            {canCheckManually(row) ? (
                              <button
                                type="button"
                                data-testid={`aichain-manual-${row.id}`}
                                disabled={busy}
                                onClick={() => setManualRowId(row.id)}
                                className="rounded border border-sky-200 px-2.5 py-1 text-xs font-semibold text-sky-700 hover:bg-sky-50 disabled:opacity-40"
                              >
                                <i className="fa-solid fa-user-pen mr-1" aria-hidden />
                                {row.manualCheck ? 'Edit manual check' : 'Check manually'}
                              </button>
                            ) : null}
                            {/* E06-F05-T15 — technical: closed until clicked, hidden
                              for non-admins by the one audience switch. */}
                            {canSeeRaw ? (
                              <button
                                type="button"
                                data-testid={`aichain-sources-${row.id}`}
                                data-raw-details="true"
                                title={`${RAW_DETAILS_LABEL}: the pages collected for the AI check`}
                                onClick={() => setSourcesRowId(row.id)}
                                className="rounded border border-dashed border-slate-300 px-2.5 py-1 text-xs text-slate-500 hover:bg-slate-50"
                              >
                                <i className="fa-solid fa-wrench mr-1" aria-hidden /> Raw sources
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                      {explainOpen ? (
                        <ScoreExplainDetailsRow
                          explanation={explanation}
                          rowId={row.id}
                          colSpan={5}
                          onClose={() => setExplainRowId(null)}
                        />
                      ) : null}
                    </Fragment>
                  );
                })}
                {rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={5}
                      data-testid="aichain-empty"
                      className="px-3 py-8 text-center text-xs text-gray-400"
                    >
                      {rowsQuery.isLoading
                        ? 'Loading the long-list…'
                        : 'No companies on this request yet — run discovery first.'}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>

          {onReview ? (
            <div className="mt-6 flex justify-end">
              <button
                type="button"
                data-testid="aichain-review-btn"
                onClick={onReview}
                className="rounded border border-slate-200 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50"
              >
                Review long-list <i className="fa-solid fa-arrow-right ml-1" aria-hidden />
              </button>
            </div>
          ) : null}
        </div>
      </div>

      {/* the paid-run confirmation — names the exact count (acceptance #2), the chain of the
          verdict path in force and the price estimate (E12-F17-T06) */}
      <ConfirmDialog
        open={confirmOpen}
        title="Start the paid AI check?"
        message={
          <>
            {confirmMessage(liveSelection.length, quote.data?.verdictEngine ?? null)}
            <span data-testid="aichain-confirm-price" className="mt-2 block font-medium">
              {aiCheckPriceLine(liveSelection.length, quote.data, quote.isError)}
            </span>
            {unconfirmedNote ? (
              <span data-testid="aichain-confirm-unconfirmed" className="mt-2 block text-amber-700">
                {unconfirmedNote}
              </span>
            ) : null}
          </>
        }
        confirmLabel={`Run ${liveSelection.length}`}
        cancelLabel="Cancel"
        testId="aichain-confirm"
        onConfirm={handleConfirmedBatch}
        onCancel={() => setConfirmOpen(false)}
      />

      {manualRow ? (
        <ManualCheckDrawer
          key={manualRow.id}
          row={manualRow}
          saving={manualCheck.isPending}
          onSave={(body) => handleSaveManual(manualRow.id, body)}
          onClose={() => setManualRowId(null)}
        />
      ) : null}

      {canSeeRaw ? (
        <SourcesPanel
          requestId={requestId}
          row={sourcesRow}
          onClose={() => setSourcesRowId(null)}
        />
      ) : null}
    </div>
  );
}

export default ResearchAiChainView;
